// Commander's one Devices page (ops plan 2026-10-17-commander-devices, ledger L684; Andrew, 2026-10-17; the query's
// shape coordinator-nodded 2026-10-17): every thing at a location on one list. A register or kitchen-screen record and
// a hub join their equipment record on the link activation and a hub claim write (`deviceId`, `nodeId`); equipment with
// no software (a router, printer or card terminal, or anything in stock) is a row of its own; a record with no
// equipment (an emulator) is a row with no serial. Joined, filtered, counted and paged on the server, so a location with
// more devices than a page still shows each exactly once. Cloud only, Radish staff only (`fleet.read`), one audit entry
// per call. The three lists it joins (fleet.staff.devices.list, fleet.staff.nodes.list, fleet.staff.equipment.list)
// stay as they are.
import { z } from 'zod';
import { defineQuery } from '../../define/operations.ts';
import { DeviceId, IsoInstant, LocationId } from '../../primitives/codecs.ts';
import { EquipmentKind, EquipmentStatus, HubReachState, StaffDeviceView } from './index.ts';

const INVENTORY_CLOUD = "Radish staff watch every location's devices and Radish's equipment from the cloud, which receives every unit's heartbeat and every hub's beacon and keeps the inventory.";

/** What a row is: an equipment kind, or a display record no equipment kind names. */
export const InventoryKind = z.enum(['register', 'handheld', 'kds', 'menu_display', 'customer_display', 'hub', 'router', 'printer', 'card_terminal']).meta({ id: 'InventoryKind' });

/** The status chips: one at a time, or none for any status. */
export const InventoryStatusFilter = z.enum(['problem', 'connected', 'not_connected', 'in_stock', 'out_for_repair', 'checked_out']).meta({ id: 'InventoryStatusFilter' });

/**
 * How the row's software reaches Radish (core inventoryRowGlance): a unit's connection, or a hub's reach in the same
 * words (`checking`: the cloud only just restarted, never shown as offline). Null when the row has no software.
 */
export const InventoryConnection = z.enum(['connected', 'not_connected', 'hub_offline', 'checking', 'never']).meta({ id: 'InventoryConnection' });

/** One thing wrong with the row's software (core InventoryProblem), with the facts its sentence needs. */
export const InventoryProblemView = z
  .object({
    kind: z.enum(['not_connected', 'uploads_waiting', 'hub_offline', 'hub_alarm', 'hub_uploads_waiting']),
    /** Since when (not connected, hub offline), when known. */
    since: IsoInstant.nullable(),
    /** How many uploads wait. */
    count: z.int().nonnegative().nullable(),
    /** The hub's alarm code. */
    alarm: z.string().nullable(),
    /** The oldest held upload's age in seconds (a hub's). */
    oldestSeconds: z.number().nonnegative().nullable(),
  })
  .meta({ id: 'InventoryProblemView' });

const DevicePart = z.object({
  source: z.literal('device'),
  deviceId: DeviceId, deviceName: z.string(), formFactor: StaffDeviceView.shape.formFactor,
  hardwareModel: z.string().nullable(), appVersion: z.string().nullable(),
  lastHeardAt: IsoInstant.nullable(), heardVia: z.enum(['cloud', 'node']).nullable(), pendingCount: z.int().nonnegative().nullable(), gateway: z.string().nullable(),
  /** A KDS screen's printer (StaffDeviceView `kdsPrinter`): its name, or null with none. Absent on other rows and from an older server. */
  kdsPrinter: StaffDeviceView.shape.kdsPrinter,
});
const HubPart = z.object({
  source: z.literal('hub'),
  nodeId: z.uuid(), serial: z.string(), reach: HubReachState, lastHeardAt: IsoInstant.nullable(), alarms: z.array(z.string()),
});

/** The equipment record's part: what Radish owns, its serial, where it is and who has it. */
const EquipmentPart = z.object({
  equipmentId: z.uuid(), kind: EquipmentKind, serial: z.string(), model: z.string(), status: EquipmentStatus, notes: z.string().nullable(),
  placeId: z.uuid().nullable(), placeName: z.string().nullable(), kitId: z.uuid().nullable(), kitName: z.string().nullable(),
  rmsDeviceId: z.string().nullable(),
  /** Its linked device's or hub's last check-in, or its router's as RMS last reported it (read only for this page's rows). */
  lastSeenAt: IsoInstant.nullable(),
});

export const InventoryRowView = z
  .object({
    /** Stable across reads: `device:<id>`, `hub:<id>` or `equipment:<id>`, by the row's software when it has some. */
    rowId: z.string().max(80),
    kind: InventoryKind,
    /** Where it is: null for an item in stock, out for repair or checked out with no store. */
    accountId: z.uuid().nullable(), accountName: z.string().nullable(), locationId: LocationId.nullable(), locationName: z.string().nullable(),
    /** The location's IANA time zone, so Commander shows times in the location's own time. */
    timezone: z.string().nullable(),
    /** The register, kitchen screen, display or hub that checks in, or null for hardware that never does. */
    software: z.discriminatedUnion('source', [DevicePart, HubPart]).nullable(),
    /** Its equipment record, or null when none was recorded (an emulator, a record without a serial). */
    equipment: EquipmentPart.nullable(),
    connection: InventoryConnection.nullable(),
    /** Worst first; empty when nothing is wrong. */
    problems: z.array(InventoryProblemView),
  })
  .meta({ id: 'InventoryRowView' });
export type InventoryRowView = z.output<typeof InventoryRowView>;

const KindCounts = z.partialRecord(InventoryKind, z.int().nonnegative());
const StatusCounts = z.partialRecord(InventoryStatusFilter, z.int().nonnegative());

export const StaffDevicesInventory = defineQuery({
  name: 'fleet.staff.devices.inventory',
  summary: "Commander's Devices for Radish staff: every register, kitchen screen, hub, router, printer and card terminal, one row per thing, each record joined to its equipment record, with its connection, problems, serial and status, searched, filtered by kind and status, and paged on the server.",
  input: z.object({
    /** One location's rows only. */
    locationId: LocationId.optional(),
    /** Words found anywhere in the row's name, serial, model, app version, location, account, notes, place or kit, ignoring case. */
    search: z.string().trim().max(100).optional(),
    kinds: z.array(InventoryKind).min(1).max(9).optional(),
    status: InventoryStatusFilter.optional(),
    /** Records never set up (never heard, no equipment record) and retired items are hidden and counted unless asked for. */
    show: z.object({ neverSetUp: z.boolean().default(false), retired: z.boolean().default(false) }).default({ neverSetUp: false, retired: false }),
    page: z.object({ offset: z.int().nonnegative().max(100_000).default(0), limit: z.int().positive().max(200).default(20) }).default({ offset: 0, limit: 20 }),
  }),
  output: z.object({
    rows: z.array(InventoryRowView),
    /** The rows shown across every page (paging counts these). */
    matching: z.int().nonnegative(),
    /**
     * Each chip's count: kinds under the status chosen, statuses under the kinds chosen (both under the location, the
     * search and what is shown). A kind or status with no row is absent.
     */
    counts: z.object({ kinds: KindCounts, statuses: StatusCounts }),
    /** Rows that matched but are hidden, for "12 never set up and 0 retired, hidden". */
    hidden: z.object({ neverSetUp: z.int().nonnegative(), retired: z.int().nonnegative() }),
    asOf: IsoInstant,
  }),
  permission: 'fleet.read',
  availability: 'cloud',
  cloudReason: INVENTORY_CLOUD,
  scope: 'platform',
  errors: [],
  // Claude already reads a location's devices and equipment through the Broker (coordinator 2026-10-17).
  agent: { expose: false },
  legacy: ['GET /v1/commander/devices', 'GET /v1/commander/kds-devices'],
});

export const inventoryQueries = [StaffDevicesInventory] as const;
